import { View } from "@-/com.npmjs.react-native";
import { SnapSlider } from "@-/dev.improve.haniverse.components.ui.snap-slider";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  DEFAULT_SLIDE_TEXT_SIZE,
  SLIDE_TEXT_SIZES,
  slideTextSizeLabel,
} from "@-/dev.improve.haniverse.lib.slide.text-size";
import { LearnFlashcardHorizontalGestureBlocker } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";

/**
 * Lets the onboarding welcome slide offer the slide text size: a {@link SnapSlider} over
 * `SLIDE_TEXT_SIZES`, between a small and a large "A", bound to the same `slideTextSize` device
 * setting as the Learn toolbar's More menu. Every stop applies immediately as the thumb snaps to
 * it, so the slide's own text previews the size while the learner drags.
 *
 * The two "A"s use fixed arbitrary sizes on purpose: `text-*` tokens inside a slide resize with the
 * setting, which would change the track's width under the finger mid-drag. Optional `subtext` is
 * the same — fixed tiny muted type under the track — so a hint like "you can change this later"
 * stays compact instead of eating a full body-paragraph slot that grows with the setting.
 *
 * The slide is a Learn card, whose horizontal pan would otherwise read a drag along the slider as a
 * swipe to the next card; the {@link LearnFlashcardHorizontalGestureBlocker} keeps touches that start on
 * the slider away from the card.
 */
export function SlideTextSizeSlider({ subtext }: { subtext?: string }) {
  const [slideTextSize, setSlideTextSize] = useDeviceSetting("slideTextSize");

  return (
    <LearnFlashcardHorizontalGestureBlocker>
      <View className="gap-1.5 px-2">
        <View className="flex-row items-center gap-3">
          <Text
            aria-hidden={true}
            className="text-[13px] leading-[18px] font-semibold text-muted-foreground"
          >
            A
          </Text>
          <SnapSlider
            accessibilityLabel="Text size"
            className="flex-1"
            index={SLIDE_TEXT_SIZES.indexOf(slideTextSize)}
            onIndexChange={(index) => {
              setSlideTextSize(SLIDE_TEXT_SIZES[index] ?? DEFAULT_SLIDE_TEXT_SIZE);
            }}
            stopCount={SLIDE_TEXT_SIZES.length}
            testID="slide-text-size-slider"
            valueLabel={slideTextSizeLabel(slideTextSize)}
          />
          <Text
            aria-hidden={true}
            className="text-[24px] leading-[30px] font-semibold text-muted-foreground"
          >
            A
          </Text>
        </View>
        {subtext === undefined ? null : (
          <Text className="text-center text-[12px] leading-[16px] text-muted-foreground/70">
            {subtext}
          </Text>
        )}
      </View>
    </LearnFlashcardHorizontalGestureBlocker>
  );
}
